import { apiUrl } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Pencil,
  Save,
  Sparkles,
  Trash2,
  Undo2,
  Redo2,
  Square,
  GripVertical,
  Columns,
  Type,
  Plus,
  FileDown,
  Code2,
  Play,
  ChevronDown,
  Maximize2,
  ExternalLink,
  X,
} from 'lucide-react';
import { Spinner } from '@/components/primitives';
import { BlockRenderer } from '@/components/blocks/BlockRenderer';
import { BlockSkeleton, DocumentSkeleton } from '@/components/blocks/BlockSkeleton';
import {
  applyOperations,
  blankWorkflow,
  tokenContext,
  workflowFromTemplate,
} from '@/lib/workflow';
import type { Block, BlockLayout, Workflow, WorkflowInputDef } from '@/lib/workflow';
import { exportHtml, exportPdf, openInNewTab } from '@/lib/exportWorkflow';
import { findExample } from '@/lib/workedExamples';
import { findTemplate } from '@/lib/workflowTemplates';
import { getWorkflow, saveWorkflow } from '@/lib/workflowStore';
import { sendAgentTurn } from '@/lib/agentClient';
import { prettyQuarter, shortPlatform } from '@/lib/format';
import styles from './WorkflowCanvas.module.css';

interface ChatEntry {
  role: 'user' | 'agent';
  text: string;
  /** Renders the bubble in an error tone; the turn produced no operations. */
  error?: boolean;
}

interface TitleOption {
  parrot_id: string;
  title: string;
  availability_platform: string;
}

/**
 * What the canvas opens on, resolved from the route alone.
 *
 * Pure, and separate from the component, so opening a workflow is a value the
 * render can compute rather than a sequence of setStates an effect has to
 * replay. `chat` is omitted — not emptied — when the route names a saved
 * workflow, because that transcript belongs to the session, not the route.
 */
function initialCanvasState(
  id: string | undefined,
  search: URLSearchParams
): { workflow: Workflow; chat?: ChatEntry[] } {
  if (id) {
    const existing = getWorkflow(id);
    if (existing) return { workflow: existing };
  }

  /* A worked example is a template with its inputs already bound — it opens
     on a rendered analysis rather than an empty form, which is the whole
     point of leading with one. */
  const example = findExample(search.get('example') ?? undefined);
  if (example) {
    const base = findTemplate(example.templateId);
    if (base) {
      const instance = workflowFromTemplate(base, example.inputs);
      return {
        workflow: { ...instance, name: `${example.name} — ${example.analysis}` },
        chat: [
          {
            role: 'agent',
            text: `${example.name} — ${example.analysis}. ${example.blurb} Change the scope above, or ask me to add to it.`,
          },
        ],
      };
    }
  }

  const template = findTemplate(search.get('template') ?? undefined);
  if (template) {
    // A template may need no input at all (Market Leaderboard), so the
    // prompt is conditional rather than reading inputs[0] blind.
    const needed = template.inputs.map((i) => i.label.toLowerCase()).join(' and ');
    return {
      workflow: workflowFromTemplate(template),
      chat: [
        {
          role: 'agent',
          text: needed
            ? `${template.name} is ready. ${template.description} Pick a ${needed} to run it.`
            : `${template.name} is ready. ${template.description}`,
        },
      ],
    };
  }

  const seed = search.get('prompt');
  return {
    workflow: blankWorkflow(seed ? seed.slice(0, 60) : 'New workflow'),
    /* Just the user's turn — the reply comes from the agent, dispatched by the
       effect below once the workflow exists. */
    chat: seed ? [{ role: 'user', text: seed }] : [],
  };
}

/* Shown in the empty transcript. Each one is something the stand-in agent can
   actually build, so a first-time click never dead-ends. */
const SUGGESTIONS = [
  'Add a breakdown by region',
  'Show the trend over time',
  'Add a ranked table',
];

/**
 * Workflow creator — chat panel plus canvas.
 *
 * The canvas is a document: blocks flow top to bottom, no drag or resize.
 * Hovering a block reveals Refine, which seeds the chat scoped to that block —
 * selection sets what the next instruction is about.
 */
export function WorkflowCanvas() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [search] = useSearchParams();

  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [history, setHistory] = useState<Workflow[]>([]);
  const [future, setFuture] = useState<Workflow[]>([]);
  const [view, setView] = useState<'preview' | 'blocks'>('preview');
  const [chat, setChat] = useState<ChatEntry[]>([]);
  const [message, setMessage] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // ─── Load: saved workflow, template instance, or blank ────────────────────
  /* Derived from the route, so it is computed during render rather than in an
     effect. An effect whose body is entirely setState renders once with the
     wrong state and again with the right one — here that first pass is an
     empty canvas, so the document visibly flashes on every open. Assigning
     during render is React's sanctioned form: it re-renders before committing,
     so the empty pass is never painted. */
  const routeKey = `${id ?? ''}|${search.toString()}`;
  const [loadedKey, setLoadedKey] = useState<string | undefined>(undefined);

  if (loadedKey !== routeKey) {
    const next = initialCanvasState(id, search);
    setLoadedKey(routeKey);
    setWorkflow(next.workflow);
    /* Absent when opening a saved workflow — its transcript is whatever the
       session already had, not a fresh greeting. */
    if (next.chat) setChat(next.chat);
  }

  const template = findTemplate(workflow?.templateId);

  /**
   * The subject control.
   *
   * Normally a template declares it. A workflow started from the describe box
   * has no template, so it used to get no control at all — and if the agent
   * bound a subject with `setInput`, nothing on screen would show it or let
   * you change it. Falling back to a definition derived from the bound key
   * keeps the subject visible and swappable however the workflow was created.
   */
  const inputDef = useMemo((): WorkflowInputDef | undefined => {
    if (template?.inputs[0]) return template.inputs[0];
    const bound = Object.keys(workflow?.inputs ?? {})[0];
    if (!bound) return undefined;
    const labels: Record<string, WorkflowInputDef> = {
      titleId: { key: 'titleId', label: 'Title', type: 'title', required: false },
      platform: { key: 'platform', label: 'Platform', type: 'platform', required: false },
      search: { key: 'search', label: 'Subject', type: 'search', required: false },
    };
    return labels[bound] ?? { key: bound, label: 'Subject', type: 'search', required: false };
  }, [template, workflow?.inputs]);

  /* Blocks that carry one of these in their params wait for a value instead of
     querying without it — otherwise Franchise Assessment with no franchise
     renders the whole catalogue. */
  const requiredInputs = useMemo(
    () => [
      ...(template?.inputs ?? []).filter((i) => i.required).map((i) => i.key),
      /* Derived tokens are required in the same sense: a comps block must wait
         for the demand band rather than querying without it and briefly
         showing the whole catalogue under a "comparable titles" heading.
         `isBlockReady` only checks tokens a block actually references, so
         listing them here affects nothing else. */
      'demand',
      'contentType',
      'genre',
    ],
    [template]
  );

  /* The subject the workflow is *about*, still unanswered. Drives the canvas
     prompt and the emphasis on the subject control. */
  const awaitingSubject = Boolean(
    inputDef?.required && !(workflow?.inputs[inputDef.key] ?? '').trim()
  );
  /**
   * Facts about the subject that only a fetch reveals.
   *
   * `{{titleId}}` alone can't build a comparable set — matching one needs the
   * subject's demand band and content type, and nothing in the workflow knows
   * them. Fetching the title once and exposing them as tokens is what lets a
   * *template* say "titles in the same demand band" rather than "the biggest
   * titles", which is the difference between a comp set and a leaderboard.
   */
  const { data: subjectMeta } = useQuery({
    queryKey: ['subject-meta', workflow?.inputs.titleId],
    enabled: Boolean(workflow?.inputs.titleId),
    queryFn: async () => {
      const res = await fetch(apiUrl(`/titles/${workflow!.inputs.titleId}`));
      if (!res.ok) throw new Error('Failed to load subject metadata');
      return res.json() as Promise<{
        demand_label?: string;
        content_type?: string;
        main_genre?: string;
        release_year?: number;
      }>;
    },
    staleTime: 30 * 60 * 1000,
  });

  const context = useMemo(
    () =>
      workflow
        ? {
            ...tokenContext(workflow),
            demand: subjectMeta?.demand_label ?? '',
            contentType: subjectMeta?.content_type ?? '',
            genre: subjectMeta?.main_genre ?? '',
          }
        : {},
    [workflow, subjectMeta]
  );

  // ─── Subject options ─────────────────────────────────────────────────────
  const { data: titleOptions } = useQuery({
    queryKey: ['workflow-titles'],
    enabled: inputDef?.type === 'title',
    queryFn: async (): Promise<TitleOption[]> => {
      const res = await fetch(apiUrl('/leaderboard?timePeriod=all&limit=40'));
      if (!res.ok) throw new Error('Failed to load titles');
      return (await res.json()).data;
    },
    staleTime: 30 * 60 * 1000,
  });


  // ─── Mutation helpers — one path in, so undo covers everything ────────────

  /* The live document, readable synchronously. Two callers need it: an async
     agent turn, which must not apply operations to the workflow as it was when
     the request was sent, and `commit` itself, which would otherwise have to
     nest state updates inside a setState updater — where StrictMode's double
     invocation pushes every history entry twice. */
  const workflowRef = useRef<Workflow | null>(null);
  useEffect(() => {
    workflowRef.current = workflow;
  }, [workflow]);

  const commit = (next: Workflow | ((current: Workflow) => Workflow)) => {
    const current = workflowRef.current;
    if (!current) return;
    const updated = typeof next === 'function' ? next(current) : next;
    setHistory((h) => [...h.slice(-19), current]);
    setFuture([]);
    setWorkflow(updated);
    /* Kept in step so two commits in one tick build on each other. */
    workflowRef.current = updated;
    /* Any edit makes the saved copy stale, so the button stops claiming
       "Saved" and offers the save again. */
    setSavedAt(null);
  };

  const undo = () => {
    const current = workflowRef.current;
    if (history.length === 0 || !current) return;
    const previous = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setFuture([current, ...future]);
    setWorkflow(previous);
    workflowRef.current = previous;
    setSavedAt(null);
  };

  const redo = () => {
    const current = workflowRef.current;
    if (future.length === 0 || !current) return;
    const [next, ...rest] = future;
    setHistory([...history, current]);
    setFuture(rest);
    setWorkflow(next);
    workflowRef.current = next;
    setSavedAt(null);
  };

  const refine = (blockTitle: string) => {
    setMessage(`In "${blockTitle}" — `);
    inputRef.current?.focus();
  };

  // ─── Document editing ─────────────────────────────────────────────────────

  const patchBlock = (blockId: string, patch: Partial<Block>) =>
    commit((current) => ({
      ...current,
      blocks: current.blocks.map((b) => (b.id === blockId ? { ...b, ...patch } : b)),
    }));

  /* Cycles full → half → third → full. A menu for three options costs more
     clicks than it saves; the label always shows the current state. */
  const cycleLayout = (block: Block) => {
    const order: BlockLayout[] = ['full', 'half', 'third'];
    const next = order[(order.indexOf(block.layout ?? 'full') + 1) % order.length];
    patchBlock(block.id, { layout: next });
  };

  const addText = (variant: 'heading' | 'body') =>
    commit((current) =>
      applyOperations(current, [
        {
          op: 'add',
          block: {
            type: 'text',
            title: variant === 'heading' ? 'Section' : 'Note',
            content: '',
            layout: 'full',
            options: { variant },
          },
        },
      ])
    );

  /* Drag to reorder, expressed as the `move` op the agent already uses — so a
     dragged block and an agent-moved block are one undo step of the same kind. */
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);

  const dropOn = (targetId: string) => {
    const current = workflowRef.current;
    setDragId(null);
    setDropId(null);
    if (!current || !dragId || dragId === targetId) return;
    const from = current.blocks.findIndex((b) => b.id === dragId);
    const to = current.blocks.findIndex((b) => b.id === targetId);
    if (from < 0 || to < 0) return;
    commit(applyOperations(current, [{ op: 'move', blockId: dragId, delta: to - from }]));
  };

  // ─── Present ──────────────────────────────────────────────────────────────

  /**
   * Reading mode: the document with every trace of the editor removed — no
   * chat, no scope row, no hover actions, no app shell. "See the full output"
   * needs the output to be the only thing on screen.
   *
   * The flag goes on `document.body` rather than staying local because the
   * sidebar and top nav belong to the layout shell, well outside this page.
   */
  const [presenting, setPresenting] = useState(false);
  const [presentMenu, setPresentMenu] = useState(false);

  useEffect(() => {
    if (presenting) document.body.dataset.presenting = 'true';
    else delete document.body.dataset.presenting;
    return () => {
      delete document.body.dataset.presenting;
    };
  }, [presenting]);

  const exitPresent = () => {
    setPresenting(false);
    if (document.fullscreenElement) void document.exitFullscreen();
  };

  /* Escape leaves. Fullscreen has its own exit, and leaving it should leave
     reading mode too — otherwise you're stranded in a chromeless page with no
     obvious way back. */
  useEffect(() => {
    if (!presenting) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitPresent();
    };
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) setPresenting(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, [presenting]);

  // ─── Export ───────────────────────────────────────────────────────────────

  const canvasRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const runExport = async (kind: 'pdf' | 'html') => {
    if (exporting) return;
    setExporting(true);
    try {
      if (kind === 'pdf') {
        await exportPdf();
      } else if (canvasRef.current) {
        await exportHtml({
          node: canvasRef.current,
          name: workflow?.name ?? 'Workflow',
          subtitle: scopeLine,
        });
      }
    } finally {
      setExporting(false);
    }
  };

  const present = async (mode: 'tab' | 'fullscreen' | 'new-tab') => {
    setPresentMenu(false);

    if (mode === 'new-tab') {
      if (!canvasRef.current) return;
      await openInNewTab({
        node: canvasRef.current,
        name: workflow?.name ?? 'Workflow',
        subtitle: scopeLine,
      });
      return;
    }

    setPresenting(true);
    if (mode === 'fullscreen' && !document.fullscreenElement) {
      /* Rejects when the gesture isn't trusted; reading mode is still on, so
         a refused fullscreen degrades to in-tab rather than failing. */
      await document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  /* Follow the newest turn. Pinned to the bottom rather than smooth-scrolled:
     the transcript is short and a smooth scroll on every append reads as drift. */
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat, pending]);

  /* Grow the composer with its content, up to a ceiling — past that it scrolls
     rather than eating the transcript. */
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [message]);

  /* There is deliberately no abort-on-unmount effect here.
   *
   * A `useEffect(() => () => abortRef.current?.abort(), [])` looks right — drop
   * the request when the page is abandoned — but StrictMode's simulated
   * unmount runs that cleanup on mount, and a cleanup cannot tell the two
   * apart. Once the workflow became available during render rather than in an
   * effect, the seed turn started inside the very flush StrictMode tears down,
   * so every describe-box prompt was cancelled the moment it was sent: the
   * server did the work and answered with operations, and the client had
   * already thrown the response away and printed "Stopped."
   *
   * What it bought was avoiding one wasted in-flight request. What it cost was
   * the feature. Setting state after unmount is a no-op and no longer warns, so
   * the trade isn't close. Cancellation stays user-initiated, via `abort`. */

  /**
   * One turn, from any source.
   *
   * Split out from `send` so the describe-box prompt can be dispatched too.
   * Arriving from the home page used to seed the transcript with the user's
   * sentence and a canned "not wired up yet" reply — written when there was no
   * agent — which meant the very first thing a new workflow did was drop the
   * request on the floor.
   */
  const runTurn = async (text: string) => {
    const current = workflowRef.current;
    if (!text || pending || !current) return;

    setPending(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const turn = await sendAgentTurn({ message: text, workflow: current }, controller.signal);
      if (turn.operations.length > 0) {
        /* Through the same path as the canvas's own controls, so one undo step
           covers the whole turn and the agent has no privileged access. The
           updater form applies to the document as it is *now* — the user may
           have changed scope while the turn was in flight. */
        commit((current) => applyOperations(current, turn.operations));
      }
      if (turn.message) setChat((c) => [...c, { role: 'agent', text: turn.message }]);
    } catch (error) {
      const aborted = error instanceof DOMException && error.name === 'AbortError';
      setChat((c) => [
        ...c,
        aborted
          ? { role: 'agent', text: 'Stopped.' }
          : { role: 'agent', text: "That didn't go through. Try again in a moment.", error: true },
      ]);
    } finally {
      setPending(false);
      abortRef.current = null;
    }
  };

  const send = async () => {
    const text = message.trim();
    if (!text) return;
    setChat((c) => [...c, { role: 'user', text }]);
    setMessage('');
    await runTurn(text);
  };

  /* The describe-box prompt, dispatched once the blank workflow exists.
     `seedSentRef` guards against StrictMode's double effect invocation — two
     turns for one sentence would double-bill and duplicate every block. */
  const seedSentRef = useRef<string | null>(null);
  useEffect(() => {
    const seed = search.get('prompt');
    if (!seed || !workflow || seedSentRef.current === seed) return;
    seedSentRef.current = seed;
    void runTurn(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow, search]);

  const abort = () => abortRef.current?.abort();

  if (!workflow) {
    return (
      <div className={styles.loading}>
        <Spinner size="lg" />
      </div>
    );
  }

  /* Three input kinds resolve their display label differently; an IIFE with
     early returns reads top-to-bottom where a nested ternary would not. */
  const subjectLabel = (() => {
    if (inputDef?.type === 'search') return workflow.inputs[inputDef.key] || undefined;
    if (inputDef?.type === 'title') {
      return titleOptions?.find((t) => t.parrot_id === workflow.inputs.titleId)?.title;
    }
    if (workflow.inputs.platform) return shortPlatform(workflow.inputs.platform);
    return undefined;
  })();

  const scopeLine = [
    workflow.scope.region || 'All regions',
    workflow.scope.timePeriod === 'all'
      ? 'All time'
      : prettyQuarter(workflow.scope.timePeriod),
  ].join(' · ');



  return (
    <div className={styles.page} data-presenting={presenting || undefined}>
      {/* Reading mode's only fixed chrome: a small pill, not a full-width bar.
          A bar spanning the viewport means the document scrolls underneath it
          and the block that gets clipped is always the cover. The pill sits
          over the top-right corner, where the banner carries only backdrop. */}
      {presenting && (
        <button
          type="button"
          className={styles.presentExit}
          onClick={exitPresent}
          data-export-hide
        >
          <X size={13} /> Exit
          <kbd className={styles.kbd}>Esc</kbd>
        </button>
      )}

      {/* ── Header owns the artifact ─────────────────────────────────────── */}
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate('/workflows')}
          aria-label="Back to workflows"
        >
          <ArrowLeft size={16} />
        </button>

        {renaming ? (
          <input
            className={styles.nameInput}
            value={workflow.name}
            autoFocus
            onChange={(e) => {
              /* Not through commit — a rename shouldn't cost an undo step per
                 keystroke — but it still makes the saved copy stale. */
              setWorkflow({ ...workflow, name: e.target.value });
              setSavedAt(null);
            }}
            onBlur={() => setRenaming(false)}
            onKeyDown={(e) => e.key === 'Enter' && setRenaming(false)}
          />
        ) : (
          <button
            type="button"
            className={styles.name}
            onClick={() => setRenaming(true)}
            title="Rename workflow"
          >
            {workflow.name}
            {subjectLabel && <span className={styles.nameSubject}> — {subjectLabel}</span>}
            <Pencil size={12} className={styles.nameIcon} />
          </button>
        )}

        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={undo}
            disabled={history.length === 0}
            aria-label="Undo"
          >
            <Undo2 size={15} />
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={redo}
            disabled={future.length === 0}
            aria-label="Redo"
          >
            <Redo2 size={15} />
          </button>

          <div className={styles.viewToggle} role="group" aria-label="Canvas view">
            {(['preview', 'blocks'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                className={`${styles.viewBtn} ${view === mode ? styles.viewBtnActive : ''}`}
                onClick={() => setView(mode)}
                aria-pressed={view === mode}
              >
                {mode === 'preview' ? 'Preview' : 'Blocks'}
              </button>
            ))}
          </div>

          {/* Present, with the three destinations the reader might want. */}
          <div className={styles.presentWrap}>
            <button
              type="button"
              className={styles.presentBtn}
              onClick={() => setPresentMenu((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={presentMenu}
            >
              <Play size={13} /> Present
              <ChevronDown size={13} />
            </button>
            {presentMenu && (
              <>
                {/* Catches the click that dismisses the menu, so it closes on
                    any outside click without a document-level listener. */}
                <span
                  className={styles.menuScrim}
                  onClick={() => setPresentMenu(false)}
                  aria-hidden="true"
                />
                <div className={styles.menu} role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.menuItem}
                    onClick={() => void present('tab')}
                  >
                    <Maximize2 size={14} /> In this tab
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.menuItem}
                    onClick={() => void present('fullscreen')}
                  >
                    <Play size={14} /> Fullscreen
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.menuItem}
                    onClick={() => void present('new-tab')}
                  >
                    <ExternalLink size={14} /> New tab
                  </button>
                </div>
              </>
            )}
          </div>

          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => void runExport('pdf')}
            disabled={exporting}
            aria-label="Export as PDF"
            title="Export as PDF"
          >
            <FileDown size={15} />
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => void runExport('html')}
            disabled={exporting}
            aria-label="Export as HTML"
            title="Export as HTML"
          >
            <Code2 size={15} />
          </button>

          <button
            type="button"
            className={styles.saveBtn}
            onClick={() => {
              saveWorkflow(workflow);
              setSavedAt(new Date().toLocaleTimeString());
            }}
          >
            {savedAt ? <Check size={14} /> : <Save size={14} />}
            {savedAt ? 'Saved' : 'Save'}
          </button>
        </div>
      </header>

      <div className={styles.split}>
        {/* ── Chat panel ─────────────────────────────────────────────────── */}
        <aside className={styles.chatPanel}>
          <div className={styles.transcript} ref={transcriptRef}>
            {chat.length === 0 && (
              <div className={styles.chatEmpty}>
                <Sparkles size={18} className={styles.chatEmptyIcon} />
                <p className={styles.chatEmptyText}>
                  Describe what you want on the canvas, or hover a block and choose Refine
                  to talk about just that block.
                </p>
                <div className={styles.chatSuggestions}>
                  {SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      className={styles.chatSuggestion}
                      onClick={() => {
                        setMessage(suggestion);
                        inputRef.current?.focus();
                      }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {chat.map((entry, i) => (
              <div
                key={i}
                className={`${styles.bubble} ${
                  entry.role === 'user' ? styles.bubbleUser : styles.bubbleAgent
                } ${entry.error ? styles.bubbleError : ''}`}
              >
                {entry.text}
              </div>
            ))}
            {pending && (
              <div className={`${styles.bubble} ${styles.bubbleAgent} ${styles.typing}`}>
                <span className={styles.dot} />
                <span className={styles.dot} />
                <span className={styles.dot} />
                <span className={styles.srOnly}>Working…</span>
              </div>
            )}
          </div>

          <div className={styles.composer}>
            <textarea
              ref={inputRef}
              className={styles.composerInput}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder={pending ? 'Working…' : 'Ask Plexie…'}
              rows={1}
            />
            {pending ? (
              <button
                type="button"
                className={styles.composerStop}
                onClick={abort}
                aria-label="Stop"
                title="Stop"
              >
                <Square size={12} fill="currentColor" />
              </button>
            ) : (
              <button
                type="button"
                className={styles.composerSend}
                onClick={() => void send()}
                disabled={!message.trim()}
                aria-label="Send"
              >
                <ArrowRight size={15} />
              </button>
            )}
          </div>
        </aside>

        {/* ── Canvas ─────────────────────────────────────────────────────── */}
        <main className={styles.canvas}>
          {/* In reading mode the document gets a real title block, in flow, so
              it scrolls with the content instead of hovering over it. It also
              matches what the HTML export and the new-tab view produce, which
              is the point of Present: see what the reader will see. */}
          {presenting && (
            <header className={styles.docHead}>
              <h1 className={styles.docTitle}>{workflow.name}</h1>
              <p className={styles.docScope}>{scopeLine}</p>
            </header>
          )}

          {/* The scope, stated rather than offered as controls.
              Region and period dropdowns lived here; they were removed, so the
              document now says what it covers and changing it is a request
              ("show me EMEA", "just Q1"), handled by `setScope`. */}
          {!presenting && (
            <p className={styles.canvasScope}>
              {subjectLabel && <span className={styles.scopeSubject}>{subjectLabel}</span>}
              {subjectLabel && <span className={styles.scopeSep} aria-hidden="true">·</span>}
              {scopeLine}
            </p>
          )}

          {view === 'blocks' ? (
            <pre className={styles.blocksJson}>
              {JSON.stringify(
                { scope: workflow.scope, inputs: workflow.inputs, blocks: workflow.blocks },
                null,
                2
              )}
            </pre>
          ) : workflow.blocks.length === 0 && pending ? (
            /* Composing. The empty state below would otherwise tell someone who
               has just described what they want to describe what they want, and
               offer them a button out of the page — while the agent is actually
               building their document. */
            <DocumentSkeleton label="Composing your document…" />
          ) : workflow.blocks.length === 0 ? (
            <div className={styles.canvasEmpty}>
              <h2 className={styles.canvasEmptyTitle}>Nothing on the canvas yet</h2>
              <p className={styles.canvasEmptyText}>
                Start from a template, or describe what you want in the chat.
              </p>
              <button
                type="button"
                className={styles.canvasEmptyBtn}
                onClick={() => navigate('/workflows')}
              >
                Browse templates
              </button>
            </div>
          ) : (
            <div className={styles.blocks} ref={canvasRef}>
              {/* One callout, not one per block. The blocks below each show
                  their own awaiting state; without this the reader gets four
                  identical placeholders and no route out of them. */}
              {awaitingSubject && inputDef && (
                <div className={styles.awaiting} role="status">
                  <span className={styles.awaitingIcon} aria-hidden="true">
                    <Sparkles size={15} />
                  </span>
                  <div>
                    <p className={styles.awaitingTitle}>
                      Choose a {inputDef.label.toLowerCase()} to run this workflow
                    </p>
                    <p className={styles.awaitingText}>
                      {template?.name} is ready — the blocks below fill in as soon as
                      they have a subject.
                    </p>
                  </div>
                </div>
              )}
              {workflow.blocks.map((block) => (
                <section
                  key={block.id}
                  className={styles.block}
                  data-type={block.type}
                  data-layout={block.layout ?? 'full'}
                  data-dragging={dragId === block.id || undefined}
                  data-dropping={dropId === block.id && dragId !== block.id ? '' : undefined}
                  onDragOver={(e) => {
                    if (!dragId) return;
                    e.preventDefault();
                    setDropId(block.id);
                  }}
                  onDragLeave={() => setDropId((id) => (id === block.id ? null : id))}
                  onDrop={(e) => {
                    e.preventDefault();
                    dropOn(block.id);
                  }}
                >
                  <header className={styles.blockHeader}>
                    {/* A banner carries the subject's own name at display size
                        — a heading above it would say it twice. Text blocks
                        are their own heading. Both keep the actions. */}
                    {block.type !== 'banner' && block.type !== 'text' && (
                      <div>
                        <h3 className={styles.blockTitle}>{block.title}</h3>
                        {block.subtitle && (
                          <p className={styles.blockSubtitle}>{block.subtitle}</p>
                        )}
                      </div>
                    )}
                    <div className={styles.blockActions} data-export-hide>
                      {/* Only the handle is draggable, not the whole block —
                          otherwise selecting text inside one starts a drag. */}
                      <span
                        className={styles.dragHandle}
                        draggable
                        onDragStart={() => setDragId(block.id)}
                        onDragEnd={() => {
                          setDragId(null);
                          setDropId(null);
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Reorder ${block.title}`}
                        title="Drag to reorder"
                      >
                        <GripVertical size={13} />
                      </span>
                      <button
                        type="button"
                        className={styles.blockAction}
                        onClick={() => cycleLayout(block)}
                        title="Change width"
                      >
                        <Columns size={12} /> {block.layout ?? 'full'}
                      </button>
                      {block.type !== 'text' && (
                        <button
                          type="button"
                          className={styles.blockAction}
                          onClick={() => refine(block.title)}
                        >
                          <Pencil size={12} /> Refine
                        </button>
                      )}
                      <button
                        type="button"
                        className={styles.blockAction}
                        onClick={() =>
                          commit(
                            applyOperations(workflow, [
                              { op: 'remove', blockId: block.id },
                            ])
                          )
                        }
                        aria-label={`Remove ${block.title}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </header>

                  <BlockRenderer
                    block={block}
                    context={context}
                    requiredInputs={requiredInputs}
                    /* Withheld while presenting — reading mode should be
                       read-only, and the renderer treats a missing handler as
                       exactly that. Same mechanism export relies on. */
                    onEditContent={
                      block.type === 'text' && !presenting
                        ? (content) => patchBlock(block.id, { content })
                        : undefined
                    }
                  />
                </section>
              ))}

              {/* Iterating on a document that already has blocks. A single
                  ghost card is enough — the existing blocks stay put and
                  readable, and the space where the next one lands is claimed
                  so the page doesn't jump when it arrives. */}
              {pending && (
                <div className={styles.composing} data-export-hide>
                  <span className={styles.composingDot} aria-hidden="true" />
                  <BlockSkeleton type="chart" />
                </div>
              )}

              {/* Adding prose is a document action, so it lives with the
                  document rather than in the chat. */}
              <div className={styles.addRow} data-export-hide>
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => addText('heading')}
                >
                  <Type size={13} /> Add heading
                </button>
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => addText('body')}
                >
                  <Plus size={13} /> Add text
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
